import { createWithEqualityFn } from 'zustand/traditional'
import { reject } from 'lodash-es'
import { shallow } from 'zustand/shallow'
import { getGraphQLErrors } from 'lib/helpers/getGraphQLErrors/getGraphQLErrors'
import { isNetworkError } from 'lib/helpers/isNetworkError/isNetworkError'
import { hashSnackbarContent } from './helpers/hashSnackbarContent/hashSnackbarContent'
import { getUpdatedSnackbars } from './helpers/getUpdatedSnackbars/getUpdatedSnackbars'
import { DEFAULT_ERROR_TEXT, CONNECTION_ERROR_TEXT } from './useSnackbarsStore.consts'
import type { Snackbar, SnackbarOptions, SnackbarVariant, SnackbarBaseOptions } from './useSnackbarsStore.types'

interface SnackbarsStore {
  snackbars: Snackbar[]
  showSnackbar(variant: SnackbarVariant, message: Snackbar['message'], options?: SnackbarOptions): Snackbar
  hideSnackbar(id: string): void
  updateSnackbar(
    id: string,
    params: Partial<Omit<SnackbarBaseOptions, 'data-testid'> & Pick<Snackbar, 'message'>>,
  ): Snackbar | undefined
  showAlert(message: Snackbar['message'], options?: SnackbarOptions): Snackbar
  showWarning(message: Snackbar['message'], options?: SnackbarOptions): Snackbar
  showInfo(message: Snackbar['message'], options?: SnackbarOptions): Snackbar
  showUpsell(message: Snackbar['message'], options?: SnackbarOptions): Snackbar
  showError(message: Snackbar['message'], options?: SnackbarOptions): Snackbar
  /**
   * Shows one snackbar per GraphQL error the server returned, a connection-error snackbar when the
   * request never reached it, or `text` for anything else.
   */
  showApolloOrTextError(err: unknown, text?: Snackbar['message'], options?: SnackbarOptions): void
}

export const useSnackbarsStore = createWithEqualityFn<SnackbarsStore>(
  (set, get) => ({
    snackbars: [],
    showSnackbar: (variant, message, options) => {
      const newSnackbar = {
        id: hashSnackbarContent(variant, String(message)),
        variant,
        message,
        options,
      }

      set(({ snackbars }) => ({ snackbars: getUpdatedSnackbars({ snackbars, newSnackbar }) }))

      return newSnackbar
    },
    updateSnackbar: (id, { message, ...options }) => {
      const snackbars = get().snackbars
      const currentSnackbar = snackbars.find((s) => s.id === id)

      if (currentSnackbar) {
        const updatedSnackbar = { ...currentSnackbar, message, options: { ...currentSnackbar.options, ...options } }

        set(({ snackbars }) => ({ snackbars: getUpdatedSnackbars({ snackbars, newSnackbar: updatedSnackbar }) }))

        return updatedSnackbar
      }
    },
    hideSnackbar: (id) => set(({ snackbars }) => ({ snackbars: reject(snackbars, { id }) })),
    showAlert: (message, options) => get().showSnackbar('alert', message, options),
    showWarning: (message, options) => get().showSnackbar('warning', message, options),
    showInfo: (message, options) => get().showSnackbar('info', message, options),
    showUpsell: (message, options) => get().showSnackbar('upsell', message, options),
    showError: (message, options) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[Snackbar]: ', message)
      }
      return get().showSnackbar('error', message, options)
    },
    showApolloOrTextError: (err, text = DEFAULT_ERROR_TEXT, options = {}) => {
      const { showError } = get()
      const messages = getGraphQLErrors(err).map(({ message }) => message)

      if (messages.length) {
        messages.forEach((message) => showError(message, options))
        return
      }

      if (isNetworkError(err)) {
        showError(CONNECTION_ERROR_TEXT, options)
        return
      }

      showError(text, options)
    },
  }),
  shallow,
)
